import { useEffect } from 'react';

import { useDownloadOobeeSDK } from './useDownloadOobeeSDK';
import { useOnOobeeError } from './useOnOobeeError';
import { useOnOobeeHint } from './useOnOobeeHint';
import { useRequestOobeePacket } from './useRequestOobeePacket';

export const useOobeeIntegration = ({
    sdk,
    enable,
    mobileClientURL,
    serviceURL,
    oobeeURL,
    license,
    identifierType,
    transactionID,
    userId,
    useTransparentPopup,
    onConnect,
    setShowOverlay,
    receivePacket,
    triggerGeolocation
}) => {
    const downloadOobeeSDK = useDownloadOobeeSDK({ mobileClientURL });
    const requestOobeePacket = useRequestOobeePacket({
        sdk,
        license,
        transactionID,
        userId,
        identifierType,
        serviceURL,
        oobeeURL
    });
    const onError = useOnOobeeError({ sdk, transactionID, setShowOverlay, useTransparentPopup });
    const onHint = useOnOobeeHint({
        sdk,
        transactionID,
        setShowOverlay,
        useTransparentPopup,
        triggerGeolocation
    });

    /**
     * Ensures, that proper callbacks are connected to GeoComply SDK.
     */
    useEffect(() => {
        if (!enable || !sdk) {
            return;
        }

        sdk.events.on('*.failed', onError);
        sdk.events.on(sdk.EVENTS.GEOLOCATION_SUCCESS, receivePacket);
        sdk.events.on(sdk.EVENTS.HINT, onHint);

        return () => {
            sdk.events.off('*.failed', onError);
            sdk.events.off(sdk.EVENTS.GEOLOCATION_SUCCESS, receivePacket);
            sdk.events.off(sdk.EVENTS.HINT, onHint);
        };
    }, [sdk, enable, onError, onHint, receivePacket]);

    useEffect(() => {
        if (!enable || !sdk) {
            return;
        }

        const disconnect = () => sdk.stopUpdating();
        window.addEventListener('beforeunload', disconnect);

        onConnect();

        return () => {
            window.removeEventListener('beforeunload', disconnect);
            sdk.stopUpdating();
        };
    }, [sdk, enable, serviceURL, oobeeURL, onConnect]);

    return { requestOobeePacket, downloadOobeeSDK };
};
